/**
 * Server-Side Conversions card.
 *
 * The per-account gate for uploading booking conversions to Google Ads from our
 * servers instead of relying on the customer's browser. OFF BY DEFAULT — nothing
 * reaches a client's Ads account until an operator turns this on, and any single
 * account can be switched back off without touching the others.
 *
 * The switch is only half the configuration: the account's "Converted"
 * conversion action ID is needed too, and the two are validated independently so
 * "on but not configured" surfaces as a visible warning rather than a silent
 * no-op. (The edge function records that same state as a `skipped_disabled` row,
 * so it's answerable from the database as well.)
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Info, Loader2, ServerCog } from 'lucide-react'

import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'

const SETTING_KEYS = {
  ENABLED: 'google_ads_server_conversions_enabled',
  ACTION_ID: 'google_ads_action_id_converted',
  CUSTOMER_ID: 'google_ads_customer_id',
} as const

interface ServerSideConversionsCardProps {
  accountId: string | undefined
}

export function ServerSideConversionsCard({ accountId }: ServerSideConversionsCardProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [enabled, setEnabled] = useState(false)
  const [actionId, setActionId] = useState('')
  const [actionIdError, setActionIdError] = useState<string | null>(null)
  const [customerId, setCustomerId] = useState('')
  const [customerIdError, setCustomerIdError] = useState<string | null>(null)

  const { data: settings, isLoading } = useQuery({
    queryKey: ['server-side-conversions', accountId],
    queryFn: async () => {
      if (!accountId) return {} as Record<string, string>

      const { data, error } = await supabase
        .from('account_settings')
        .select('key, value')
        .eq('account_id', accountId)
        .in('key', Object.values(SETTING_KEYS))

      if (error) throw error

      const map: Record<string, string> = {}
      data?.forEach((row) => {
        map[row.key] = row.value ?? ''
      })
      return map
    },
    enabled: Boolean(accountId),
  })

  useEffect(() => {
    if (!settings) return
    // Exactly 'true' counts as on — the same rule the edge function applies, so
    // the UI can never disagree with what actually gates the upload.
    setEnabled(settings[SETTING_KEYS.ENABLED] === 'true')
    setActionId(settings[SETTING_KEYS.ACTION_ID] ?? '')
    setCustomerId(settings[SETTING_KEYS.CUSTOMER_ID] ?? '')
  }, [settings])

  const mutation = useMutation({
    mutationFn: async () => {
      if (!accountId) throw new Error('No account found')

      const rows = [
        {
          account_id: accountId,
          key: SETTING_KEYS.ENABLED,
          value: enabled ? 'true' : 'false',
          description:
            'Upload booking conversions to Google Ads from the server (per-account gate)',
          is_secret: false,
        },
        {
          account_id: accountId,
          key: SETTING_KEYS.ACTION_ID,
          value: actionId.trim() || null,
          description: 'Google Ads "Converted" conversion action ID for server-side uploads',
          is_secret: false,
        },
        {
          account_id: accountId,
          key: SETTING_KEYS.CUSTOMER_ID,
          // Google displays customer IDs dashed (123-456-7890); the API wants
          // digits. Normalise on save so either form can be pasted in.
          value: customerId.replace(/-/g, '').trim() || null,
          description: 'Google Ads customer ID (the client operating account)',
          is_secret: false,
        },
      ]

      for (const row of rows) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(row, { onConflict: 'account_id,key' })
        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['server-side-conversions', accountId] })
      queryClient.invalidateQueries({ queryKey: ['account-settings', accountId] })
      toast({
        title: 'Server-side conversions saved',
        description: enabled
          ? 'Paid bookings will be reported to Google Ads from our servers.'
          : 'Server-side conversion uploads are turned off for this account.',
      })
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const handleSave = () => {
    const trimmedAction = actionId.trim()
    const trimmedCustomer = customerId.replace(/-/g, '').trim()

    // Google rejects anything that isn't a bare numeric ID — a pasted resource
    // name or tag snippet is the likely mistake, so catch it here rather than
    // letting every conversion fail permanently at the API.
    let valid = true
    if (trimmedAction && !/^\d+$/.test(trimmedAction)) {
      setActionIdError('Must be digits only — the numeric ID, not the full resource name.')
      valid = false
    } else {
      setActionIdError(null)
    }

    if (trimmedCustomer && !/^\d+$/.test(trimmedCustomer)) {
      setCustomerIdError('Must be digits only, e.g. 1234567890 (dashes are fine).')
      valid = false
    } else {
      setCustomerIdError(null)
    }

    if (!valid) return
    mutation.mutate()
  }

  const enabledWithoutAction = enabled && !actionId.trim()

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ServerCog className="h-5 w-5" />
          Server-Side Conversions
        </CardTitle>
        <CardDescription>
          Report paid bookings to Google Ads directly from our servers, so conversions
          aren&apos;t lost to ad blockers, browser privacy settings or a closed tab.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="flex items-start justify-between gap-6 rounded-lg border p-4">
          <div className="space-y-1">
            <Label htmlFor="server-conversions-enabled" className="text-base">
              Upload conversions from our servers
            </Label>
            <p className="text-sm text-muted-foreground">
              Off by default. The existing website tag keeps firing either way, so
              turning this on adds a reliable second path rather than replacing one.
            </p>
          </div>
          <Switch
            id="server-conversions-enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="converted-action-id">Converted conversion action ID</Label>
          <Input
            id="converted-action-id"
            value={actionId}
            onChange={(e) => {
              setActionId(e.target.value)
              setActionIdError(null)
            }}
            placeholder="e.g. 7123456789"
            inputMode="numeric"
          />
          <p className="text-sm text-muted-foreground">
            The numeric ID of the &quot;Converted&quot; conversion action in this
            account&apos;s Google Ads. Digits only — not the full resource name.
          </p>
          {actionIdError && <p className="text-sm text-destructive">{actionIdError}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="ads-customer-id">Google Ads customer ID</Label>
          <Input
            id="ads-customer-id"
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value)
              setCustomerIdError(null)
            }}
            placeholder="e.g. 1234567890"
            inputMode="numeric"
          />
          <p className="text-sm text-muted-foreground">
            The client&apos;s own Google Ads account, shown top-right in Google Ads.
            Filled in automatically for accounts set up through onboarding — enter it
            by hand for any account that wasn&apos;t. Dashes are fine; they&apos;re
            stripped on save. The account must be linked under our manager (MCC)
            account or uploads will fail with an auth error.
          </p>
          {customerIdError && <p className="text-sm text-destructive">{customerIdError}</p>}
        </div>

        {enabledWithoutAction && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Server-side conversions are switched on but no conversion action ID is
              set, so nothing will be uploaded. Add the ID above to complete the setup.
            </AlertDescription>
          </Alert>
        )}

        {enabled && !customerId.trim() && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              No Google Ads customer ID is set for this account, so nothing will be
              uploaded. Enter it above (or run the Google Ads onboarding step, which
              fills it in automatically).
            </AlertDescription>
          </Alert>
        )}

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Conversions can take several hours to appear in the Google Ads interface.
            Every upload attempt is recorded, including failures, so &quot;did this
            booking reach Google?&quot; is answerable straight away.
          </AlertDescription>
        </Alert>

        <Button onClick={handleSave} disabled={mutation.isPending || !accountId}>
          {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save
        </Button>
      </CardContent>
    </Card>
  )
}
